import { ChangeEvent, KeyboardEvent, useCallback, useEffect, useState } from 'react';
import { Input } from '@/shared/ui/Input/Input';
import useDebounce from '@/shared/lib/hooks/useDebounce';
import { useOutsideClick } from '@/shared/lib/hooks/useOutsideClick';
import { useLazyGetRegionsQuery } from '@/features/RegionSearch';
import s from './RegionInlineSearch.module.scss';

interface RegionInlineSearchProps {
  value: string;
  onChange: (e: string | ChangeEvent<HTMLInputElement>) => void;
  onPick?: (value: string) => void;
  onClose?: () => void;
  placeholder?: string;
  className?: string;
}

export const RegionInlineSearch = (props: RegionInlineSearchProps) => {
  const { value, onChange, onPick, onClose, placeholder = 'Введите регион', className } = props;
  const [open, setOpen] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const debounceValue = useDebounce(value, 500);
  const [trigger, { data, isFetching }] = useLazyGetRegionsQuery();

  const handleClose = useCallback(() => {
    setOpen(false);
    onClose?.();
  }, [onClose]);

  const onKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        handleClose();
      }
    },
    [handleClose],
  );

  const ref = useOutsideClick(open, handleClose);

  useEffect(() => {
    if (hasInteracted && debounceValue) {
      trigger(debounceValue);
      setOpen(true);
    }
  }, [hasInteracted, debounceValue]);

  const regionFormat = (region: string) => {
    if (region) return region.split('_')[1];
    return '';
  };

  const onSelect = (item: string) => {
    const next = regionFormat(item);
    onChange(next);
    onPick?.(next);
    setOpen(false);
  };

  const onSelectAnotherRegion = () => {
    onChange('-');
    onPick?.('-');
    setOpen(false);
  };

  return (
    <div className={s.RegionInlineSearch} ref={ref} onKeyDown={open ? onKeyDown : undefined}>
      <Input
        value={value}
        onChange={(e) => {
          setHasInteracted(true);
          onChange(e);
        }}
        placeholder={placeholder}
        error={''}
        isSubmitted={false}
        baseAddon={false}
        className={className ?? s.stringInput}
        onFocus={() => {
          setHasInteracted(true);
          setOpen(true);
        }}
      />
      {open &&
        debounceValue &&
        !isFetching &&
        regionFormat(data?.regions?.[0] ?? '') !== debounceValue &&
        debounceValue !== '-' && (
          <div className={s.dropdown}>
            <div className={s.wrapper}>
              <>
                <button type={'button'} className={s.item} onClick={onSelectAnotherRegion}>
                  Другой регион
                </button>
                {data?.regions?.map((item: string) => {
                  if (debounceValue !== regionFormat(item))
                    return (
                      <button key={item} type={'button'} className={s.item} onClick={() => onSelect(item)}>
                        {regionFormat(item)}
                      </button>
                    );
                  return null;
                })}
              </>
            </div>
          </div>
        )}
    </div>
  );
};
